import React, { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../../Components/Navbar";
import Button from "@mui/material/Button";
import { useParams, useLocation } from "react-router";
import SingleDICOMViewer from "./SingleDICOMViewer";
import { API_URL } from "../../Endpoints";
import { useDispatch, useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import cornerstone from "cornerstone-core";
import cornerstoneMath from "cornerstone-math";
import cornerstoneTools from "cornerstone-tools";
import Hammer from "hammerjs";
import dicomParser from "dicom-parser";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import cornerstoneWADOImageLoaderWebWorker from "cornerstone-wado-image-loader/dist/index.worker.bundle.min.worker.js";
import cornerstoneWebImageLoader from "cornerstone-web-image-loader";
import { styled } from "@mui/system";
import ToolButtons from "./ToolButtons";
import {
  hydrateAnnotationsForRestore,
  getEtdrsFirstEntry,
  getEtdrsVolumesFromAnnotation,
  getEtdrsCenterFromAnnotation,
  normalizeOpAnnotationsForRedux,
} from "./utils/annotationFormat";
import {
  FLUID_SEGMENTATION_LABELS,
  getFluidLabelsFromVolumesData,
} from "./utils/fluidManualBrush";
import { bumpAnnotationSourceVolumesToken } from "./actions/dcm_actions";

// Modified Tools
import ProbeTool from "./tools/annotation/Probe";
import RectangleRoiTool from "./tools/annotation/RectangleRoiTool";
import LengthTool from "./tools/annotation/LengthTool";
import EllipticalRoiTool from "./tools/annotation/EllipticalRoiTool";
import LineTool from "./tools/annotation/LineTool";

// Custom Tools
import ThicknessMap from "./tools/ThicknessMap";

// Static Tools (Just for visualizing)
import LineStatic from "./tools/LineStatic";
import RectangleStatic from "./tools/RectangleStatic";
import AiSegmentationTool from "./tools/annotation/AiSegmentation";
import FluidSegmentationTool from "./tools/annotation/FluidSegmentation";
import TextStatic from "./tools/TextStatic";
import CircleStatic from "./tools/CircleStatic";

import CloudSyncIcon from "@mui/icons-material/CloudSync";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import SaveIcon from "@mui/icons-material/Save";

import Splitter from "@devbookhq/splitter";
import AnnotationsData from "./Annotations/AnnotationsData";

import SyncIcon from "@mui/icons-material/Sync";
import AddStackDialog from "../../Components/AddStackDialog";
import HelpDialog from "./Dialogs/HelpDialog";

import {
  opMetadataFetch,
  optMetadataFetch,
  save_annotations,
} from "./actions/dcm_actions";
import { extractLocalDicomMetaTable } from "./utils/localDicomMetaExtract";
import { logApiOptMetadata } from "./utils/dicom_utils";
import LoaderScreen from "../../Components/LoaderScreen";
import AnnotationSaveSnackbar from "../../Components/AnnotationSaveSnackbar";
import MetaDataDialog from "./Dialogs/DicomMetaData";
import NavbarWithMeta from "../../Components/NavbarWithMeta";
import LisonTool from "./tools/annotation/LisonTool";
import LisonProbe from "./tools/annotation/LisonProbe";
import Etdrs from "./tools/Etdrs";
import AiSegmentationEraser from "./tools/AiSegmentationEraser";

cornerstoneTools.external.cornerstone = cornerstone;

// Dicom tools
cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWebImageLoader.external.cornerstone = cornerstone;

cornerstoneTools.external.Hammer = Hammer;

// Modified Tools
cornerstoneTools.LisonProbeTool = LisonProbe;
cornerstoneTools.ProbeTool = ProbeTool;
cornerstoneTools.RectangleRoiTool = RectangleRoiTool;
cornerstoneTools.LengthTool = LengthTool;
cornerstoneTools.LisonTool = LisonTool;
cornerstoneTools.EllipticalRoiTool = EllipticalRoiTool;
cornerstoneTools.LineTool = LineTool;

// Custom Tools
cornerstoneTools.ThicknessMap = ThicknessMap;
cornerstoneTools.Etdrs = Etdrs;

// Static tools (Just for drawing)
cornerstoneTools.LineStatic = LineStatic;
cornerstoneTools.RectangleStatic = RectangleStatic;
cornerstoneTools.AiSegmentationTool = AiSegmentationTool;
cornerstoneTools.FluidSegmentationTool = FluidSegmentationTool;
cornerstoneTools.TextStatic = TextStatic;
cornerstoneTools.CircleStatic = CircleStatic;

const useWadoWebWorkers = false;
let wadoWorkerInitialized = false;

const initWadoWebWorkers = () => {
  if (!useWadoWebWorkers || wadoWorkerInitialized) {
    return;
  }

  cornerstoneWADOImageLoader.webWorkerManager.initialize({
    maxWebWorkers:
      typeof navigator !== "undefined" && navigator.hardwareConcurrency
        ? navigator.hardwareConcurrency
        : 1,
    startWebWorkersOnDemand: true,
    webWorkerPath: cornerstoneWADOImageLoaderWebWorker,
    taskConfiguration: {
      decodeTask: {
        initializeCodecsOnStartup: true,
      },
    },
  });

  wadoWorkerInitialized = true;
};

cornerstoneWADOImageLoader.configure({
  beforeSend: function (xhr) {
    // Cors
    //xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
    xhr.setRequestHeader(
      "Access-Control-Allow-Headers",
      "Origin, X-Requested-With, Content-Type, Accept",
    );

    // Add custom headers here (e.g. auth tokens)
    //xhr.setRequestHeader('x-auth-token', 'my auth token');
  },
  useWebWorkers: useWadoWebWorkers,
});

initWadoWebWorkers();

// Some Zeiss OP files store JPEG2000 codestreams in PixelData without encapsulation.
// Cornerstone expects encapsulated pixel data for JPEG2000; add a fallback to extract it.
const originalGetPixelData = cornerstoneWADOImageLoader.wadouri.getPixelData;
cornerstoneWADOImageLoader.wadouri.getPixelData = (dataSet, frameIndex = 0) => {
  const transferSyntax = dataSet?.string?.("x00020010");
  const isJpeg2000 =
    transferSyntax === "1.2.840.10008.1.2.4.90" ||
    transferSyntax === "1.2.840.10008.1.2.4.91";
  const pixelElement =
    dataSet?.elements?.x7fe00010 || dataSet?.elements?.x7fe00008;

  if (isJpeg2000 && pixelElement && !pixelElement.encapsulatedPixelData) {
    const offset = pixelElement.dataOffset || 0;
    const length = pixelElement.length || 0;

    if (length > 0) {
      let frame = new Uint8Array(dataSet.byteArray.buffer, offset, length);

      // If the codestream marker isn't at the start, try to locate it.
      if (!(frame[0] === 0xff && frame[1] === 0x4f)) {
        let start = -1;
        for (let i = 0; i < frame.length - 1; i += 1) {
          if (frame[i] === 0xff && frame[i + 1] === 0x4f) {
            start = i;
            break;
          }
        }
        if (start > 0) {
          frame = frame.slice(start);
        }
      }

      return frame;
    }
  }

  return originalGetPixelData(dataSet, frameIndex);
};

const originalWadouriLoadImage = cornerstoneWADOImageLoader.wadouri.loadImage;
cornerstone.registerImageLoader("wadouri", (imageId, options = {}) => {
  const parsed = cornerstoneWADOImageLoader.wadouri.parseImageId(imageId);
  const frameIndex = parsed.frame || 0;

  const loadPromise =
    cornerstoneWADOImageLoader.wadouri.dataSetCacheManager.load(
      parsed.url,
      cornerstoneWADOImageLoader.internal.xhrRequest,
      imageId,
    );

  const promise = loadPromise.then((dataSet) => {
    const transferSyntax = dataSet.string("x00020010");
    const pixelElement =
      dataSet.elements.x7fe00010 || dataSet.elements.x7fe00008;
    const isJpeg2000 =
      transferSyntax === "1.2.840.10008.1.2.4.90" ||
      transferSyntax === "1.2.840.10008.1.2.4.91";

    // Use the default loader when data is well-formed encapsulated or uncompressed
    if (!isJpeg2000 || (pixelElement && pixelElement.encapsulatedPixelData)) {
      return originalWadouriLoadImage(imageId, options).promise;
    }

    if (!pixelElement || !pixelElement.length) {
      return originalWadouriLoadImage(imageId, options).promise;
    }

    const offset = pixelElement.dataOffset || 0;
    const length = pixelElement.length || 0;
    let frame = new Uint8Array(dataSet.byteArray.buffer, offset, length);

    if (!(frame[0] === 0xff && frame[1] === 0x4f)) {
      let start = -1;
      for (let i = 0; i < frame.length - 1; i += 1) {
        if (frame[i] === 0xff && frame[i + 1] === 0x4f) {
          start = i;
          break;
        }
      }
      if (start > 0) {
        frame = frame.slice(start);
      }
    }

    return cornerstoneWADOImageLoader
      .createImage(imageId, frame, transferSyntax, options)
      .then((image) => {
        image.data = dataSet;
        image.sharedCacheKey = parsed.url;
        return image;
      });
  });

  return { promise, cancelFn: undefined };
});

cornerstoneWebImageLoader.configure({
  beforeSend: function (xhr) {
    // Cors
    //xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
    xhr.setRequestHeader(
      "Access-Control-Allow-Headers",
      "Origin, X-Requested-With, Content-Type, Accept",
    );
  },
  useWebWorkers: true,
});

cornerstoneTools.init({ mouseEnabled: true });

// Register custom tools
cornerstoneTools.AiSegmentationEraser = AiSegmentationEraser;

// Register mouse tool for AiSegmentationEraser
cornerstoneTools.addTool(AiSegmentationEraser);

const LayoutContainer = styled("div")(() => ({
  height: "calc(100vh - 57px)",
  overflow: "hidden",
  width: "100%",
  backgroundColor: "rgb(220, 220, 220)",
}));

const theme = createTheme({
  palette: {
    secondary: {
      // This is green.A700 as hex.
      main: "#11cb5f",
    },
    default: {
      main: "#999",
    },
  },
});
export default function DCMViewer(props) {
  const dispatch = useDispatch();
  const dicom_data = useSelector((state) => state.dcm);

  const match = useParams();
  const id = match.id;
  let { search } = useLocation();

  const query = new URLSearchParams(search);
  const opData = dicom_data.dicom_data[query.get("stack_id")];
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [addStackDialogOpen, setAddStackDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAnnotationSourceLoading, setIsAnnotationSourceLoading] =
    useState(false);
  const [annotationSourceLoadingLabel, setAnnotationSourceLoadingLabel] =
    useState("");
  const [annotationSourceVersion, setAnnotationSourceVersion] = useState(0);
  const annotationRenderPendingRef = useRef(0);
  const keysDis = ["Shift", "Alt", "a", "A"];

  const finishAnnotationSourceLoading = useCallback(() => {
    annotationRenderPendingRef.current -= 1;
    if (annotationRenderPendingRef.current <= 0) {
      annotationRenderPendingRef.current = 0;
      setIsAnnotationSourceLoading(false);
      setAnnotationSourceLoadingLabel("");
    }
  }, []);

  const notifyAnnotationSourceRendered = useCallback(() => {
    finishAnnotationSourceLoading();
  }, [finishAnnotationSourceLoading]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!dicom_data.annotationSaveInProgress) return;
      e.preventDefault();
      e.returnValue =
        "Saving annotations in progress. Don't close this tab or you may lose changes.";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dicom_data.annotationSaveInProgress]);

  const onKeyboardKeyDown = (e) => {
    const frameCount = opData?.OPT?.metadata?.frames?.length;

    if (
      e.key === "ArrowUp" &&
      frameCount != null &&
      dicom_data.currentFrame < frameCount - 1
    ) {
      dispatch({
        type: "DICOM_CHANGE_FRAME",
        changeId: query.get("stack_id"),
        changeType: "OP",
        frame: dicom_data.currentFrame + 1,
      });
    }

    if (
      e.key === "ArrowDown" &&
      frameCount != null &&
      dicom_data.currentFrame !== 0
    ) {
      dispatch({
        type: "DICOM_CHANGE_FRAME",
        changeId: query.get("stack_id"),
        changeType: "OP",
        frame: dicom_data.currentFrame - 1,
      });
    }

    if (keysDis.includes(e.key)) {
      cornerstoneTools.setToolPassive(dicom_data.selectedTool);
      cornerstoneTools.setToolDisabled("stack");
      cornerstoneTools.setToolDisabled("StackScrollMouseWheel");
    } else if (["z", "Z"].includes(e.key)) {
      cornerstoneTools.setToolDisabled("StackScrollMouseWheel");
    }
    dispatch({
      type: "SET_SELECTED_KEY",
      key: e.key,
    });
  };

  const onKeyboardKeyUp = (e) => {
    if (keysDis.includes(e.key)) {
      cornerstoneTools.setToolActive("stack");
      cornerstoneTools.setToolActive("StackScrollMouseWheel", {});
      cornerstoneTools.setToolActive(dicom_data.selectedTool, {
        mouseButtonMask: 1,
      });
    } else if (["z", "Z"].includes(e.key)) {
      cornerstoneTools.setToolActive("StackScrollMouseWheel", {});
    }
    dispatch({
      type: "SET_SELECTED_KEY",
      key: null,
    });
  };
  useEffect(() => {
    document.addEventListener("keydown", onKeyboardKeyDown);
  }, []);
  useEffect(() => {
    document.addEventListener("keydown", onKeyboardKeyDown);
    document.addEventListener("keyup", onKeyboardKeyUp);
    return () => {
      document.removeEventListener("keydown", onKeyboardKeyDown);
      document.removeEventListener("keyup", onKeyboardKeyUp);
    };
  }, [dicom_data.selectedTool, dicom_data.currentFrame]);

  const [dicomStacks, setDicomStacks] = useState([]);
  const hasMetaTableData = (data) => Array.isArray(data) && data.length > 0;

  const fetchOpMetaData = async () => {
    const stackId = query.get("stack_id");
    const opUrl = dicom_data?.dicom_data[stackId]?.OP?.url;
    if (!opUrl || hasMetaTableData(dicom_data?.opMetaData)) return;

    let response = await opMetadataFetch(opUrl);
    if (!hasMetaTableData(response)) {
      response = await extractLocalDicomMetaTable(opUrl);
    }
    if (!hasMetaTableData(response)) return;

    dispatch({ type: "opMetaData_changed", opMetaData: response });
  };

  const fetchOptMetaData = async () => {
    const stackId = query.get("stack_id");
    const stackEntry = dicom_data?.dicom_data[stackId];
    const optUrl = stackEntry?.OPT?.url;
    if (!optUrl || hasMetaTableData(dicom_data?.optMetaData)) return;

    let responseData = await optMetadataFetch(optUrl);

    if (!hasMetaTableData(responseData)) {
      responseData = await extractLocalDicomMetaTable(
        optUrl,
        stackEntry?.OPT?.metadata?.dataset,
      );
    }
    if (!hasMetaTableData(responseData)) return;

    logApiOptMetadata(
      responseData,
      stackEntry?.OPT?.metadata,
      "DCMViewer.fetchOptMetaData",
    );

    dispatch({
      type: "optMetaData_changed",
      optMetaData: responseData,
    });
  };

  const fetchStacks = async () => {
    const response = await axios.get(
      `${API_URL}/api/v1/stacks?id=${query.get("stack_id")}&user_id=${query.get(
        "user_id",
      )}&series=${query.get("series_no")}`,
    );
  };

  const getFormParams = () => ({
    form_version_id: query.get("form_version_id")
      ? query.get("form_version_id")
      : "",
    form_type: query.get("form_version_id")
      ? "additional_form"
      : "primary_form",
  });

  const getInitialAnnotationUserId = () => {
    if (query.get("role") !== "adjudicator") {
      return query.get("user_id");
    }

    return localStorage.getItem(query.get("stack_id")) ?? query.get("user_id");
  };

  const filterSegmentLineAnnotations = (annotations) => {
    if (!annotations) {
      return;
    }

    const seriesKey = query.get("series_no");
    const annotationsToProcess = annotations[seriesKey] || annotations;

    if (annotationsToProcess && annotationsToProcess.OPT) {
      Object.keys(annotationsToProcess.OPT).forEach((url) => {
        const urlData = annotationsToProcess.OPT[url];

        if (Array.isArray(urlData.Line)) {
          return;
        }

        if (
          urlData.Line &&
          urlData.Line.data &&
          Array.isArray(urlData.Line.data)
        ) {
          urlData.Line.data = urlData.Line.data.filter(
            (item) => item.name !== "Segment",
          );

          if (urlData.Line.data.length === 0) {
            delete urlData.Line;
          }
        }
      });
    }
  };

  const getCurrentSeriesAnnotations = (annotations) => {
    if (!annotations) {
      return {};
    }

    return annotations[query.get("series_no")] || annotations;
  };

  const replaceLoadedAnnotationState = (annotations) => {
    const stackId = query.get("stack_id");
    const currentAnnotations = getCurrentSeriesAnnotations(annotations);
    const hydrated = hydrateAnnotationsForRestore(currentAnnotations);
    const normalizedOp = normalizeOpAnnotationsForRedux(hydrated.OP || {});
    const stackEntry = dicom_data?.dicom_data?.[stackId];

    if (!stackEntry?.OP || !stackEntry?.OPT) {
      return;
    }

    dispatch({ type: "Set_Lisons_Area_Data", lisonsAreasData: {} });
    dispatch({
      type: "selected_area_Changed",
      SelectedArea: { status: false, TotalArea: null, LisonsArea: "" },
    });
    dispatch({
      type: "SET_ETDRS_VOLUMES_FROM_SEGMENTATION_API",
      etdrsVolumesFromSegmentationApi: null,
    });
    dispatch({
      type: "SET_ETDRS_VALUES_FROM_SEGMENTATION_API",
      edtrsValuesFromSegmentationApi: null,
    });
    dispatch({ type: "CLEAR_ALL_FLUID_DIRTY_FRAMES" });
    dispatch({
      type: "SET_ACTIVE_FLUID_LABELS",
      activeFluidLabels: [...FLUID_SEGMENTATION_LABELS],
    });
    dispatch({ type: "SET_PREV_START_REF", payload: null });
    dispatch({
      type: "mainScreenShot_url_changed",
      mainScreenShotUrl: null,
    });
    dispatch({
      type: "SET_ANNOTATION_DATA",
      payload: {},
      id: stackId,
      key: "OPT",
    });
    dispatch({
      type: "SET_ANNOTATION_DATA",
      payload: normalizedOp,
      id: stackId,
      key: "OP",
    });

    if (hydrated.OP?.lisonsAreasData) {
      dispatch({
        type: "Set_Lisons_Area_Data",
        lisonsAreasData: hydrated.OP.lisonsAreasData,
      });
    }

    const savedVolumes = getEtdrsVolumesFromAnnotation(hydrated.OP?.Etdrs);
    if (savedVolumes) {
      const savedFluidLabels = getFluidLabelsFromVolumesData(savedVolumes);
      if (savedFluidLabels.length) {
        dispatch({
          type: "SET_ACTIVE_FLUID_LABELS",
          activeFluidLabels: savedFluidLabels,
        });
      }
      dispatch({
        type: "SET_ETDRS_VOLUMES_FROM_SEGMENTATION_API",
        etdrsVolumesFromSegmentationApi: savedVolumes,
      });
    }

    const savedCircleData = getEtdrsFirstEntry(hydrated.OP?.Etdrs)?.circleData;
    if (savedCircleData) {
      dispatch({
        type: "SET_ETDRS_VALUES_FROM_SEGMENTATION_API",
        edtrsValuesFromSegmentationApi: savedCircleData,
      });
    }

    const savedEtdrsCenter = getEtdrsCenterFromAnnotation(hydrated.OP?.Etdrs);
    if (savedEtdrsCenter) {
      dispatch({
        type: "SET_PREV_START_REF",
        payload: { ...savedEtdrsCenter },
      });
    }

    const imageIds = stackEntry.OPT?.stack?.imageIds;
    if (!Array.isArray(imageIds)) {
      dispatch({ type: "UPDATE_IMAGE" });
      return;
    }

    imageIds.forEach((imageId) => {
      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        imageId,
        {},
      );
    });

    imageIds.forEach((imageId) => {
      const frameKey = imageId.split("?").pop();
      const frameAnnotations =
        hydrated.OPT?.[imageId] ||
        hydrated.OPT?.[frameKey] ||
        {};

      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        imageId,
        frameAnnotations,
      );
    });

    dispatch({ type: "UPDATE_IMAGE" });
  };

  const fetchStackPayload = async (annotationUserId) => {
    const { form_version_id, form_type } = getFormParams();
    const response = await axios.get(
      `${API_URL}/api/v1/file?id=${query.get(
        "stack_id",
      )}&user_id=${annotationUserId}&series=${query.get(
        "series_no",
      )}&form_version_id=${form_version_id}&form_type=${form_type}`,
    );

    if (
      query.get("role") === "adjudicator" &&
      annotationUserId === query.get("user_id")
    ) {
      const adjudicatorAnnotationsResponse = await axios.get(
        `${API_URL}/api/v1/qa-form-adjudicator?stack_id=${query.get(
          "stack_id",
        )}&user_id=${query.get("user_id")}${
          query.get("form_version_id")
            ? `&form_version_id=${query.get("form_version_id")}`
            : ""
        }`,
      );

      if (adjudicatorAnnotationsResponse.data.annotations) {
        response.data.annotations =
          adjudicatorAnnotationsResponse.data.annotations;
      }
    }

    filterSegmentLineAnnotations(response.data.annotations);
    return response.data;
  };

  const handleAnnotationSourceChange = useCallback(
    async (annotationUserId, sourceLabel = "Annotations") => {
      if (query.get("role") !== "adjudicator") {
        return;
      }

      setAnnotationSourceLoadingLabel(`${sourceLabel} annotations loading`);
      setIsAnnotationSourceLoading(true);
      annotationRenderPendingRef.current = 2;
      bumpAnnotationSourceVolumesToken();
      try {
        const stackPayload = await fetchStackPayload(annotationUserId);

        if (stackPayload.status === "failed") {
          setApiError(true);
          setIsAnnotationSourceLoading(false);
          setAnnotationSourceLoadingLabel("");
          annotationRenderPendingRef.current = 0;
          return;
        }

        dispatch({
          type: "fetchedId_changed",
          fetchedId: annotationUserId,
        });
        replaceLoadedAnnotationState(stackPayload.annotations);

        setDicomStacks((currentStacks) =>
          currentStacks.map((stack) =>
            stack.id === (stackPayload.id || query.get("stack_id"))
              ? {
                  ...stackPayload,
                  id: stack.id,
                  files: stack.files,
                  basic_metadata: stack.basic_metadata,
                }
              : stack,
          ),
        );
        setAnnotationSourceVersion((version) => version + 1);
      } catch (_error) {
        setIsAnnotationSourceLoading(false);
        setAnnotationSourceLoadingLabel("");
        annotationRenderPendingRef.current = 0;
      }
    },
    [dicom_data?.dicom_data, query, dispatch],
  );

  useEffect(() => {
    if (dicomStacks[0]?.basic_metadata) {
      dispatch({
        type: "SET_AI_SEGMENTATION_DATA",
        aiSegmentationData: dicomStacks[0]?.ai_segmentation,
      });
      dispatch({
        type: "SET_FLUID_SEGMENTATION_DATA",
        fluidSegmentation: dicomStacks[0]?.fluid_segmentation || null,
      });

      const seriesKey = query.get("series_no");
      const stackAnnotations =
        dicomStacks[0]?.annotations?.[seriesKey] ?? dicomStacks[0]?.annotations;
      dispatch({
        type: "Set_BasicMetaData",
        basicMetaData: {
          ...dicomStacks[0]?.basic_metadata,
          visit_date: dicomStacks[0]?.visit_date,
        },
      });

      // Check if AI segmentation is available
      let aiSegmentationAvailable = false;

      // Check if ai_segmentation object isn't empty
      if (
        dicomStacks[0]?.ai_segmentation &&
        Object.keys(dicomStacks[0].ai_segmentation).length > 0
      ) {
        aiSegmentationAvailable = true;
      } else {
        // Check if AiSegmentation exists in any annotations.OPT key
        if (stackAnnotations && stackAnnotations.OPT) {
          // Loop through all OPT keys to find any AiSegmentation
          for (const key of Object.keys(stackAnnotations.OPT)) {
            if (stackAnnotations.OPT[key]?.AiSegmentation) {
              aiSegmentationAvailable = true;
              break;
            }
          }
        }
      }

      dispatch({
        type: "SET_AI_SEGMENTATION_AVAILABLE",
        aiSegmentationAvailable: aiSegmentationAvailable,
      });
    }

    // if (dicom_data.aiSegmentationData !== null) {

    // }
  }, [dicomStacks]);
  useEffect(() => {
    const fetchDicomStacks = async () => {
      const annotationUserId = getInitialAnnotationUserId();
      const stackPayload = await fetchStackPayload(annotationUserId);

      if (query.get("role") === "adjudicator") {
        dispatch({
          type: "fetchedId_changed",
          fetchedId: annotationUserId,
        });
      }

      if (stackPayload.status === "failed") {
        setApiError(true);
      } else {
        setDicomStacks([stackPayload]);
      }
      setIsLoading(false);
    };
    fetchDicomStacks();
    fetchStacks();
  }, []);

  useEffect(() => {
    fetchOptMetaData();
    fetchOpMetaData();
  }, [
    query.get("stack_id"),
    dicom_data?.dicom_data?.[query.get("stack_id")]?.OP?.url,
    dicom_data?.dicom_data?.[query.get("stack_id")]?.OPT?.url,
  ]);

  const addStack = (files) => {
    setDicomStacks([...dicomStacks, files]);
    setAddStackDialogOpen(false);
  };

  const removeStack = (dcm_id) => {
    dispatch({
      type: "REMOVE_STACK",
      id: dcm_id,
    });

    setDicomStacks(dicomStacks.filter((stack) => stack.id !== dcm_id));
  };

  const onSaveClick = async () => {
    setIsSaving(true);
    try {
      await save_annotations(
        dicom_data,
        query.get("stack_id"),
        query.get("user_id"),
        query.get("series_no"),
        query.get("role"),
        query.get("form_version_id") ? "additional_form" : "primary_form",
        query.get("form_version_id") ? query.get("form_version_id") : "",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const [gridSize, setGridSize] = useState([85, 15]);
  const handleResizeFinished = (ids, newSizes) => {
    //onWindowResize()
    setGridSize(newSizes);
    dispatch({
      type: "SET_GRID_WIDTH",
      width: newSizes[0],
    });
  };

  if (isLoading) {
    return (
      <div>
        <Navbar />
        <LayoutContainer>
          <div
            style={{
              display: "flex",
              textAlign: "center",
              justifyContent: "center",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <LoaderScreen />
          </div>
        </LayoutContainer>
      </div>
    );
  }

  if (apiError) {
    return (
      <div>
        <Navbar />
        <LayoutContainer>
          <div
            style={{
              display: "flex",
              textAlign: "center",
              justifyContent: "center",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <h2 style={{ color: "brown" }}>Failed to fetch stack</h2>
          </div>
        </LayoutContainer>
      </div>
    );
  }

  if (dicomStacks.length === 0) {
    return (
      <div>
        <Navbar />
        <LayoutContainer>
          <div
            style={{
              display: "flex",
              textAlign: "center",
              justifyContent: "center",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <div>No Data Found...</div>
          </div>
        </LayoutContainer>
      </div>
    );
  }
  return (
    <div>
      <NavbarWithMeta
        metadata={dicomStacks[0].basic_metadata}
        onAnnotationSourceChange={handleAnnotationSourceChange}
        annotationSourceLoading={isAnnotationSourceLoading}
      />
      <AnnotationSaveSnackbar
        open={dicom_data.annotationSaveInProgress}
        message={dicom_data.annotationSaveMessage}
      />
      {isAnnotationSourceLoading ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <LoaderScreen message={annotationSourceLoadingLabel || "Loading..."} />
        </div>
      ) : null}

      <LayoutContainer>
        <div
          style={{
            height: "45px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <ToolButtons />
          {dicom_data.canSyncScans ? (
            <div style={{ padding: 4 }}>
              <ThemeProvider theme={theme}>
                <Button
                  variant="contained"
                  style={{ marginLeft: 10 }}
                  onClick={() =>
                    dispatch({
                      type: "DICOM_SYNC_SCANS",
                      syncScans: !dicom_data.syncScans,
                    })
                  }
                  color={dicom_data.syncScans ? "primary" : "default"}
                  size="small"
                  startIcon={<SyncIcon />}
                >
                  Synchronize Scans
                </Button>
              </ThemeProvider>
            </div>
          ) : null}
          <div style={{ padding: 4 }}>
            {dicomStacks.length > 0 &&
              dicom_data.dicom_data[dicomStacks[0].id]?.OP && (
                <MetaDataDialog
                  urls={{
                    op: dicom_data.dicom_data[dicomStacks[0].id].OP.url,
                    opt: dicom_data.dicom_data[dicomStacks[0].id].OPT.url,
                  }}
                />
              )}
            <HelpDialog />

            {/* <Button
              style={{ marginLeft: 10 }}
              onClick={() => setAddStackDialogOpen(true)}
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<AddIcon />}
            >
              Add Stack
            </Button> */}
            <Button
              variant="contained"
              style={{ marginLeft: 10 }}
              color="primary"
              size="small"
              startIcon={isSaving ? <CloudSyncIcon /> : <SaveIcon />}
              onClick={() => onSaveClick()}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <Splitter
          style={{ height: "calc(100% - 40px)", padding: 0, margin: 0 }}
          onResizeFinished={handleResizeFinished}
          gutterClassName="custom-gutter-horizontal"
          initialSizes={gridSize}
        >
          <div style={{ height: "100%" }}>
            {dicomStacks.map((stack, index) => (
              <SingleDICOMViewer
                removeStack={removeStack}
                metadata={dicomStacks[0].basic_metadata}
                index={index}
                dicom_id={stack.id}
                key={`dcm-stack-${index}`}
                height={`${100 / dicomStacks.length}%`}
                urls={stack.files}
                annotations={
                  stack.annotations[query.get("series_no")] ?? stack.annotations
                }
                series_no={query.get("series_no")}
                stack={stack}
                masks={stack.masks}
                thickness_map={stack.thickness_map}
                annotationSourceVersion={annotationSourceVersion}
                onAnnotationSourceRendered={notifyAnnotationSourceRendered}
              />
            ))}
          </div>

          <div
            style={{ height: "100%", backgroundColor: "#FFF" }}
            id="form-side-bar"
          >
            <AnnotationsData
              request_info={{
                stack_id: query.get("stack_id"),
                series: query.get("series_no"),
                user_id: query.get("user_id"),
                role: query.get("role") || "grader",
              }}
            />
          </div>
        </Splitter>

        <AddStackDialog
          open={addStackDialogOpen}
          addStack={addStack}
          handleClose={() => setAddStackDialogOpen(false)}
        />
      </LayoutContainer>
    </div>
  );
}
